/** For resizable columns:
 *  https://codesandbox.io/embed/resize-columns-table-material-ui-ei0pqd?fontsize=14&hidenavigation=1&theme=dark
 */

import React from 'react'
import { Theme } from '@mui/material'
import Tooltip from 'components_new/atoms/Tooltip'

import {
  DatasetType,
  DefaultDatasetData,
  FormattedWidgetDataDataset,
  ParsedWidgetDataLabel,
  WidgetOrder
} from 'types/GlobalWidget'
import { TableDataType } from './index'
import {
  sortAlphabeticalAsc,
  sortAlphabeticalDesc,
  sortDateAsc,
  sortDateDesc,
  sortNumericAsc,
  sortNumericDesc
} from 'utils/sortHelper'
import dayjs from 'utils/dayjs'

const PATH_SEPARATOR = '__SEP__'

export const ORDER_BY_SEGMENT_BY = 'ORDER_BY_SEGMENT_BY'

export enum OrderByAlgoritmType {
  STRING = 'string',
  NUMBER = 'number',
  DATE = 'date'
}

export const getCellAlignment = (type: TableDataType) => {
  switch (type) {
  case TableDataType.ENUM: {
    return 'center'
  }
  case TableDataType.NUMBER: {
    return 'right'
  }
  case TableDataType.DATE: {
    return 'right'
  }
  case TableDataType.NUMBER_TAG: {
    return 'right'
  }
  default: {
    return 'left'
  }
  }
}

export const ellipseLabel = (
  label: string | number | null,
  n: number | null
) => {
  const stringLabel = label?.toString()

  if (n !== null && stringLabel) {
    return stringLabel.length < n ? (
      stringLabel
    ) : (
      <Tooltip title={label}>
        <span>{stringLabel.slice(0, n)}&#8230;</span>
      </Tooltip>
    )
  } else {
    return label
  }
}

/**
 * Sx for a column pinned `left` from the edge, which is 0 for the
 * first one and the width of the columns before it for the rest. An
 * opaque background is required so value columns scroll beneath it.
 * Header and footer rows paint an overlay gradient on top of the
 * widget background, so their sticky cell must repeat it to match.
 */
export const getStickyCellSx = (
  theme: Theme,
  withOverlay: boolean,
  left: number
) => ({
  position: 'sticky' as const,
  left,
  zIndex: withOverlay ? 3 : 1,
  bgcolor: theme.palette.background.widget,
  backgroundImage: withOverlay
    ? `linear-gradient(${theme.palette.background.overlay}, ${theme.palette.background.overlay})`
    : undefined
})

export const getCellScaleSx = (scaleFactor: number) => {
  return {
    fontSize: 16 * scaleFactor,
    lineHeight: '1.5em',
    px: '1.25em',
    py: '0.5em'
  }
}

export const getStatus = (
  value: number | null,
  upperThresholdValue: number | null,
  lowerThresholdValue: number | null,
  increaseIsPositive: boolean
) => {
  if (upperThresholdValue === null || value === null) {
    return 'inherit'
  }

  const success = increaseIsPositive
    ? value >= upperThresholdValue
    : value <= upperThresholdValue

  if (success) {
    return 'success'
  }

  const warning =
    lowerThresholdValue !== null &&
    (increaseIsPositive
      ? value > lowerThresholdValue
      : value < lowerThresholdValue)

  if (warning) {
    return 'warning'
  }

  return 'error'
}

/**
 * Parse segment of data type DATE
 */
const parseSegmentLabelToDate = (label: ParsedWidgetDataLabel): Date | null => {
  if ('year_quarter' === label.technical_name) {
    const [yearStr, quarterStr] = (label.label as string).split('-')
    const year = parseInt(yearStr, 10)
    const quarter = parseInt(quarterStr, 10)

    return dayjs().year(year).quarter(quarter).startOf('quarter').toDate()
  }

  if ('year_week' === label.technical_name) {
    const [yearStr, weekStr] = (label.label as string).split('-')
    const year = parseInt(yearStr, 10)
    const week = parseInt(weekStr, 10)

    return dayjs().year(year).isoWeek(week).startOf('week').toDate()
  }

  return dayjs(label.label).toDate()
}

type OrderByArray =
  | {
      values: (Date | null)[]
      algoritmType: OrderByAlgoritmType.DATE
    }
  | {
      values: (string | null)[]
      algoritmType: OrderByAlgoritmType.STRING
    }
  | {
      values: (number | null)[]
      algoritmType: OrderByAlgoritmType.NUMBER
    }

export function getOrderByArray(
  order: WidgetOrder,
  labels: ParsedWidgetDataLabel[],
  datasets: FormattedWidgetDataDataset[],
  segmentIndexOrder: 'asc' | 'desc' | null = null
): OrderByArray | null {
  if (!order.id) {
    return null
  }

  if (order.id === ORDER_BY_SEGMENT_BY) {
    if (segmentIndexOrder !== null) {
      // the rows follow a configured order (eg. account levels), so
      // toggle on each label's backend index instead of its text.
      // Negating every index mirrors the order, so an index that
      // runs against the segment order still puts the order's start
      // first when ascending.
      const direction = segmentIndexOrder === 'asc' ? 1 : -1

      return {
        values: labels.map((item) => item.index * direction),
        algoritmType: OrderByAlgoritmType.NUMBER
      }
    }

    const isDateSegment = labels[0]?.type === 'DATE'

    if (
      isDateSegment &&
      ['month', 'weekday', 'year'].includes(labels[0]?.technical_name)
    ) {
      // sort these segments as number instead of date
      return {
        values: labels.map((item) =>
          typeof item.label === 'string'
            ? parseInt(item.label, 10)
            : (item.label as number)
        ),
        algoritmType: OrderByAlgoritmType.NUMBER
      }
    }

    if (isDateSegment) {
      return {
        values: labels.map((item) => parseSegmentLabelToDate(item)),
        algoritmType: OrderByAlgoritmType.DATE
      }
    }

    return {
      values: labels.map((item) => item.display_label),
      algoritmType: OrderByAlgoritmType.STRING
    }
  }

  const dataset = datasets.find(
    (dataset) => dataset.id === order.id && dataset.index === order.index
  )

  if (!dataset) {
    return null
  }

  if (
    [DatasetType.ENUM, DatasetType.STRING, DatasetType.DATE].includes(
      dataset.type
    )
  ) {
    return {
      values: dataset.data.map((value) => (value as string[])[0] || null),
      algoritmType: OrderByAlgoritmType.STRING
    }
  }
  if (dataset.type === DatasetType.NUMBER_TAG) {
    return {
      values: dataset.data.map((value) => (value as number[])[0] ?? null),
      algoritmType: OrderByAlgoritmType.NUMBER
    }
  }

  return {
    values: dataset.data as DefaultDatasetData,
    algoritmType: OrderByAlgoritmType.NUMBER
  }
}

/**
 * Sort according to column settings and filter hidden datasets.
 */
export function sortFormattedDatasets(datasets: FormattedWidgetDataDataset[]) {
  return [...datasets]
    .sort((a, b) => {
      if (a.settingIndex === b.settingIndex) {
        return a.index - b.index
      }

      return a.settingIndex - b.settingIndex
    })
    .filter((dataset) => !dataset.hidden)
}

function sortIndexes<T>(
  values: T[],
  order: 'asc' | 'desc',
  sortAsc: (a: T, b: T) => number,
  sortDesc: (a: T, b: T) => number
): number[] {
  return values
    .map((value, index) => ({ value, index }))
    .sort((a, b) =>
      order === 'asc' ? sortAsc(a.value, b.value) : sortDesc(a.value, b.value)
    )
    .map((item) => item.index)
}

export function getSortIndexes(
  sortingOpt: OrderByArray,
  order: 'asc' | 'desc'
): number[] {
  switch (sortingOpt.algoritmType) {
  case OrderByAlgoritmType.DATE:
    return sortIndexes<Date | null>(
      sortingOpt.values,
      order,
      sortDateAsc,
      sortDateDesc
    )

  case OrderByAlgoritmType.NUMBER:
    return sortIndexes<number | null>(
      sortingOpt.values,
      order,
      sortNumericAsc,
      sortNumericDesc
    )

  case OrderByAlgoritmType.STRING:
    return sortIndexes<string | null>(
      sortingOpt.values,
      order,
      sortAlphabeticalAsc,
      sortAlphabeticalDesc
    )
  }
}

export function pathKey(path: string[]): string {
  return path.join(PATH_SEPARATOR)
}

export function getAllPrefixes<T>(arr: T[]): T[][] {
  const result: T[][] = []

  for (let i = 1; i <= arr.length; i++) {
    result.push(arr.slice(0, i))
  }

  return result
}
